
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

type Language = 'en' | 'es';

interface LanguageContextType {
  language: Language;
  setLanguage: (language: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  // Get initial language from localStorage or default to 'en'
  const [language, setLanguage] = useState<Language>(() => {
    if (typeof window === 'undefined') return 'en';
    
    try {
      const savedLanguage = localStorage.getItem('language');
      return (savedLanguage === 'en' || savedLanguage === 'es') ? savedLanguage as Language : 'en';
    } catch (e) {
      console.error('Error accessing localStorage:', e);
      return 'en';
    }
  });

  // Update localStorage when language changes
  useEffect(() => {
    try {
      if (typeof window !== 'undefined') {
        localStorage.setItem('language', language);
      }
    } catch (e) {
      console.error('Error writing to localStorage:', e);
    }
  }, [language]);

  // Translation function
  const t = (key: string): string => {
    const translations = language === 'en' ? englishTranslations : spanishTranslations;
    return translations[key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Custom hook to use the language context
export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Translation dictionaries
const englishTranslations: Record<string, string> = {
  // Navbar
  'home': 'Home',
  'partner_with_noa': 'Partner with Noa',
  'about_noa': 'About Noa',
  'contact_us': 'Contact Us',
  
  // About Page
  'about_noa_subtitle': 'ABOUT NOA',
  'trusted_partner': 'Your trusted partner',
  'trusted_partner_long': 'Your trusted partner for the long term',
  'partner_description': 'We partner with founders across Latin America to accelerate growth, preserve their legacy, and create lasting impact.',
  'scroll_to_learn_more': 'Scroll to learn more',
  
  // What We Do Section
  'about_noa_section': 'About Noa.',
  'about_description': 'We are seasoned entrepreneurs and investors specializing in acquiring and scaling successful B2B software companies across Latin America. Whether you\'re a founder planning to retire or looking to drive your business into its next chapter, we craft tailored, long-term transition plans that align with your vision while protecting your employees, customers, and legacy.',
  'benefits_of_partnering': 'Benefits of partnering with Noa',
  'permanent_home': 'A Permanent Home',
  'permanent_home_desc': 'We invest with no intention to sell, providing stability and allowing for long-term strategic planning without pressure for quick exits.',
  'resources_for_growth': 'Resources for growth',
  'resources_desc': 'Get access to the Noa Platform—resources, shared services, capital, and strategic support—to drive your company\'s next stage of growth.',
  'aligned_incentives': 'Aligned Long-Term Incentives',
  'aligned_incentives_desc': 'We create flexible ownership structures that provide founders and management teams, with meaningful equity upside, fostering alignment, accountability, and long-term success.',
  'autonomy': 'Autonomy & Ownership',
  'autonomy_desc': 'We follow a decentralized model that empowers existing teams to manage daily operations independently, while Noa Partners provides strategic support when needed.',
  'why_partner': 'Why Partner with Noa?',
  'why_partner_desc': 'Discover the unique advantages of partnering with Noa for your software business. We offer flexible terms, long-term vision, and a partnership approach unlike traditional acquisitions.',
  'learn_more': 'Learn More',
  
  // Contact CTA
  'ready_to_learn': 'Ready to learn more?',
  'connect_with_us': 'Connect with us to discuss how Noa Partners can help take your software company to the next level.',
  'schedule_call': 'Schedule a Call',
  
  // Footer
  'navigation': 'Navigation',
  'connect': 'Connect',
  'address': 'Address',
  'rights_reserved': 'All rights reserved.',
  'privacy_policy': 'Privacy Policy',
  'terms_of_service': 'Terms of Service',
  
  // Hero Section
  'noa_partners': 'NOA PARTNERS',
  'your_forever': 'Your forever',
  'partner': 'partner',
  'we_acquire': 'We seek B2B software companies delivering mission-critical solutions in niche markets.',
  
  // Why Sell Page
  'why_partner_with_noa': 'WHY PARTNER WITH NOA',
  'your_legacy': 'Your legacy',
  'our_commitment': 'our commitment',
  'we_understand': 'We understand the journey of building a successful business. Let\'s discuss how we can preserve and grow your legacy together.',
  
  // What We Offer Section
  'what_we_offer': 'What We Offer',
  'noa_platform': 'Noa Platform',
  'services_suite': 'Our comprehensive suite of services is designed to help your business thrive in today\'s competitive landscape.',
  'sales_title': 'Sales & Go-To-Market Support',
  'sales_desc': 'Access in-house developed and proven B2B SaaS playbooks to streamline and automate processes across the customer journey.',
  'engineering_title': 'Engineering & Strategic R&D',
  'engineering_desc': 'Providing support in defining AI strategies, making key product investment decisions, or hiring top engineering talent.',
  'financial_title': 'Financial Operations & Planning',
  'financial_desc': 'We help implement financial tools and automation to enhance business insights, performance tracking, and growth opportunities.',
  'partnership_title': 'Partnership & M&A',
  'partnership_desc': 'Helping businesses grow through partnerships and add-on acquisitions, expand offerings, and enter new markets.',
  'community_title': 'Noa Community',
  'community_desc': 'Gain access to a regional network of CEOs, founders, and top executives for mentorship, coaching, and leadership growth.',
  'advisory_title': 'Advisory Board',
  'advisory_desc': 'Providing governance and support for portfolio CEOs, with a focus on goal setting, performance tracking, and resource management.',
  
  // What We Look For Section
  'companies_we_seek': 'Companies We Seek To Partner With',
  'software_company': 'Software Company',
  'license_saas': 'License / SaaS',
  'revenue_range': '$1-10M',
  'annual_recurring': 'Revenue between $1-10M annually with consistent historical growth',
  'growth_rate': 'Neither Growing nor Shrinking Too Quickly',
  'growth_rate_desc': '~25% annually either way',
  'b2b_critical': 'B2B and Mission Critical',
  'product': 'Product',
  'vertical_horizontal': 'Vertical or Horizontal',
  'niche': 'Niche',
  'no_profitability': 'No Profitability Requirements',
  'less_than': 'Less than 40%',
  
  // Team Section
  'our_team': 'Our Team',
  'team_description': 'Our leadership team has decades of experience in building, acquiring, and scaling B2B software companies across Latin America. With backgrounds in entrepreneurship, private equity, and corporate innovation, we provide strategic guidance to help founders and their companies achieve their full potential.',
};

const spanishTranslations: Record<string, string> = {
  // Navbar
  'home': 'Inicio',
  'partner_with_noa': 'Asociarse con Noa',
  'about_noa': 'Sobre Noa',
  'contact_us': 'Contáctanos',
  
  // About Page
  'about_noa_subtitle': 'SOBRE NOA',
  'trusted_partner': 'Tu socio de confianza',
  'trusted_partner_long': 'Tu socio de confianza a largo plazo',
  'partner_description': 'Nos asociamos con fundadores en toda América Latina para acelerar el crecimiento, preservar su legado y crear un impacto duradero.',
  'scroll_to_learn_more': 'Desplázate para saber más',
  
  // What We Do Section
  'about_noa_section': 'Sobre Noa.',
  'about_description': 'Somos empresarios e inversionistas experimentados especializados en adquirir y escalar empresas de software B2B exitosas en toda América Latina. Ya sea que seas un fundador que planea retirarse o que busca llevar su negocio a su siguiente etapa, creamos planes de transición personalizados a largo plazo que se alinean con tu visión mientras protegemos a tus empleados, clientes y legado.',
  'benefits_of_partnering': 'Beneficios de asociarse con Noa',
  'permanent_home': 'Un Hogar Permanente',
  'permanent_home_desc': 'Invertimos sin intención de vender, proporcionando estabilidad y permitiendo la planificación estratégica a largo plazo sin presión por salidas rápidas.',
  'resources_for_growth': 'Recursos para crecer',
  'resources_desc': 'Accede a la Plataforma Noa—recursos, servicios compartidos, capital y apoyo estratégico—para impulsar la siguiente etapa de crecimiento de tu empresa.',
  'aligned_incentives': 'Incentivos Alineados a Largo Plazo',
  'aligned_incentives_desc': 'Creamos estructuras de propiedad flexibles que proporcionan a los fundadores y equipos de gestión un importante potencial de capital, fomentando la alineación, la responsabilidad y el éxito a largo plazo.',
  'autonomy': 'Autonomía y Propiedad',
  'autonomy_desc': 'Seguimos un modelo descentralizado que empodera a los equipos existentes para gestionar las operaciones diarias de forma independiente, mientras que Noa Partners proporciona apoyo estratégico cuando sea necesario.',
  'why_partner': '¿Por qué asociarse con Noa?',
  'why_partner_desc': 'Descubre las ventajas únicas de asociarse con Noa para tu empresa de software. Ofrecemos condiciones flexibles, visión a largo plazo y un enfoque de asociación diferente de las adquisiciones tradicionales.',
  'learn_more': 'Saber Más',
  
  // Contact CTA
  'ready_to_learn': '¿Listo para saber más?',
  'connect_with_us': 'Conecta con nosotros para discutir cómo Noa Partners puede ayudar a llevar tu empresa de software al siguiente nivel.',
  'schedule_call': 'Programar una Llamada',
  
  // Footer
  'navigation': 'Navegación',
  'connect': 'Conectar',
  'address': 'Dirección',
  'rights_reserved': 'Todos los derechos reservados.',
  'privacy_policy': 'Política de Privacidad',
  'terms_of_service': 'Términos de Servicio',
  
  // Hero Section
  'noa_partners': 'NOA PARTNERS',
  'your_forever': 'Tu socio de',
  'partner': 'para siempre',
  'we_acquire': 'Adquirimos empresas de software B2B exitosas en América Latina para conservar y hacerlas crecer para siempre.',
  
  // Why Sell Page
  'why_partner_with_noa': '¿POR QUÉ ASOCIARSE CON NOA?',
  'your_legacy': 'Tu legado',
  'our_commitment': 'nuestro compromiso',
  'we_understand': 'Entendemos el viaje de construir un negocio exitoso. Hablemos sobre cómo podemos preservar y hacer crecer tu legado juntos.',
  
  // What We Offer Section
  'what_we_offer': 'Lo Que Ofrecemos',
  'noa_platform': 'Plataforma Noa',
  'services_suite': 'Nuestra completa gama de servicios está diseñada para ayudar a tu empresa a prosperar en el competitivo panorama actual.',
  'sales_title': 'Ventas y Apoyo en Go-To-Market',
  'sales_desc': 'Accede a playbooks de SaaS B2B desarrollados internamente y probados para optimizar y automatizar procesos en todo el recorrido del cliente.',
  'engineering_title': 'Ingeniería e I+D Estratégica',
  'engineering_desc': 'Brindamos apoyo en la definición de estrategias de IA, tomando decisiones clave de inversión en productos o contratando talento de ingeniería de primer nivel.',
  'financial_title': 'Operaciones Financieras y Planificación',
  'financial_desc': 'Ayudamos a implementar herramientas financieras y automatización para mejorar la visión del negocio, el seguimiento del rendimiento y las oportunidades de crecimiento.',
  'partnership_title': 'Asociaciones y Fusiones & Adquisiciones',
  'partnership_desc': 'Ayudamos a las empresas a crecer a través de asociaciones y adquisiciones complementarias, ampliando ofertas y entrando en nuevos mercados.',
  'community_title': 'Comunidad Noa',
  'community_desc': 'Obtén acceso a una red regional de CEOs, fundadores y altos ejecutivos para mentoría, coaching y crecimiento de liderazgo.',
  'advisory_title': 'Consejo Asesor',
  'advisory_desc': 'Proporcionamos gobernanza y apoyo para los CEOs de cartera, con un enfoque en el establecimiento de objetivos, seguimiento del rendimiento y gestión de recursos.',
  
  // What We Look For Section
  'companies_we_seek': 'Empresas Con Las Que Buscamos Asociarnos',
  'software_company': 'Empresa de Software',
  'license_saas': 'Licencia / SaaS',
  'revenue_range': '$1-10M',
  'annual_recurring': 'Ingresos entre $1-10M anuales con crecimiento histórico constante',
  'growth_rate': 'Ni Creciendo ni Disminuyendo Demasiado Rápido',
  'growth_rate_desc': '~25% anual en cualquier dirección',
  'b2b_critical': 'B2B y Misión Crítica',
  'product': 'Producto',
  'vertical_horizontal': 'Vertical u Horizontal',
  'niche': 'Nicho',
  'no_profitability': 'Sin Requisitos de Rentabilidad',
  'less_than': 'Menos del 40%',
  
  // Team Section
  'our_team': 'Nuestro Equipo',
  'team_description': 'Nuestro equipo directivo tiene décadas de experiencia en la construcción, adquisición y escalamiento de empresas de software B2B en toda América Latina. Con experiencia en emprendimiento, capital privado e innovación corporativa, proporcionamos orientación estratégica para ayudar a los fundadores y sus empresas a alcanzar su máximo potencial.',
};
